import { Dispatch } from 'react';
import RedButton from '../../../components/Buttons/RedButton';
import createTranslateFunction from '../../../i18n/createTranslateFunction';
import { createNewOrder } from '../../../redux/slices/deliveryFormSlice';
import { AppDispatch, useSelector } from '../../../redux/store';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import { calculateTotalCartPrice } from '../../EditFoodScreen/EditFoodScreenHelperFunctions';
import { FocusContext, useFocusable } from '@noriginmedia/norigin-spatial-navigation';
import { toast, Bounce } from 'react-toastify';

type TotalSummaryBarProps = {
    focusKey: string;
    toggleCheckoutAlertModal: (errorMessage: string) => void;
};

const TotalSummaryBar = ({
    focusKey: focusKeyParam,
    toggleCheckoutAlertModal,
}: TotalSummaryBarProps) => {
    const TranslateFunction = createTranslateFunction('checkout_screen.delivery_form');
    const TranslateAlertsFunction = createTranslateFunction('checkout_screen.alerts');
    const deliveryFormData = useSelector((state) => state.deliveryFormData);
    const deliveryForm = deliveryFormData.deliveryForm;

    const branchesList = useSelector((state) => state.branches.branchesList);

    const { cart } = useSelector((state) => state.cart);
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();

    const { ref, focusKey } = useFocusable({
        focusKey: focusKeyParam,
        preferredChildFocusKey: 'PAY-NOW-BUTTON',
    });

    const showErrorToast = (message: string) => {
        toast.error(message, {
            position: 'top-center',
            theme: 'colored',
            transition: Bounce,
        });
    };

    const isAbleToCheckout = () => {
        const updatedCart = JSON.parse(localStorage.getItem('cart') || '[]');
        if (updatedCart.length < 1) {
            const errorMessage = TranslateAlertsFunction('empty_cart');
            showErrorToast(errorMessage);
            toggleCheckoutAlertModal(errorMessage);
            return false;
        }
        if (deliveryForm.order_type === 0 && !deliveryForm.address) {
            const errorMessage = TranslateAlertsFunction('empty_address');
            showErrorToast(errorMessage);
            toggleCheckoutAlertModal(errorMessage);
            return false;
        }
        if (deliveryForm.address && !deliveryForm?.address.closest_branch) {
            const errorMessage = TranslateAlertsFunction('empty_branch');
            showErrorToast(errorMessage);
            toggleCheckoutAlertModal(errorMessage);
            return false;
        }
        if (!deliveryForm.future_date) {
            const errorMessage = TranslateAlertsFunction('empty_date');
            showErrorToast(errorMessage);
            toggleCheckoutAlertModal(errorMessage);
            return false;
        }
        //If order type is delivery and price is under minimum delivery price
        const chosenBranchData = branchesList.find(
            (branch) => branch.external_branch_id === deliveryForm.branch_id,
        );
        const totalCartPrice = calculateTotalCartPrice(cart, deliveryForm.delivery_price);
        if (
            !chosenBranchData ||
            (deliveryForm.order_type === 0 &&
                totalCartPrice < Number(chosenBranchData.properties.minimum_order))
        ) {
            const errorMessage = TranslateAlertsFunction('below_minimum_order');
            showErrorToast(errorMessage);
            toggleCheckoutAlertModal(errorMessage);
            return false;
        }
        return true;
    };

    const navigateToPaymentPage = async () => {
        if (!isAbleToCheckout()) {
            return;
        }
        try {
            await dispatch(createNewOrder(deliveryForm));

            if (deliveryFormData.orderSubmissionError) {
                const errorMessage = TranslateAlertsFunction('order_submission_error');
                showErrorToast(`Order Submission Error: ${deliveryFormData.orderSubmissionError}`);
                toggleCheckoutAlertModal(errorMessage);
                return;
            }
            navigate('/choose-payment');
        } catch (error) {
            const errorMessage = TranslateAlertsFunction('order_submission_error');
            showErrorToast(
                `Order Submission Error: ${error instanceof Error ? error.message : 'Unknown error'}`,
            );
            toggleCheckoutAlertModal(errorMessage);
            navigate('/');
        }
    };

    return (
        <FocusContext.Provider value={focusKey}>
            <div
                ref={ref}
                className="h-[13vw] w-[95%] px-[2vw] bg-white bg-opacity-[3%] rounded-t-[0.5vw] flex items-center justify-between"
            >
                <div>
                    <div className="flex items-center w-[16vw] justify-between">
                        <span className="text-[1.8vw] font-semibold text-white ml-[2vw]">{`${TranslateFunction(`sub_headers.total_cost`)}`}</span>
                        <span className="text-[1.8vw] font-semibold text-white">
                            {calculateTotalCartPrice(cart, deliveryForm.delivery_price)}
                            {`₪`}
                        </span>
                    </div>
                    {deliveryForm.delivery_price > 0 && (
                        <div className="flex items-center w-[16vw] justify-between">
                            <span className="text-[1.5vw] text-white ml-[2vw]">{`${TranslateFunction(`sub_headers.delivery_cost`)}`}</span>
                            <span className="text-[1.5vw] text-white">
                                {`${deliveryForm.delivery_price}₪`}
                            </span>
                        </div>
                    )}
                </div>
                <div className={'flex items-center'}>
                    <RedButton
                        className="!text-[1.6vw] font-semibold !w-[12vw] !h-[5vw]"
                        onFocusClass={'border-[0.15vw] border-[#ffffff]'}
                        onClick={navigateToPaymentPage}
                        focusKey={'PAY-NOW-BUTTON'}
                    >{`${TranslateFunction(`buttons.pay_now`)}`}</RedButton>
                </div>
            </div>
        </FocusContext.Provider>
    );
};

export default TotalSummaryBar;
